/* eslint-disable @typescript-eslint/no-explicit-any */
import { Button } from '@mui/material';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography, { TypographyProps } from '@mui/material/Typography';
import { useNavigate } from 'react-router';

import { CalendarCoursePeriod } from 'api/hooks/private/periods/GetAllCoursePeriods.ts';
import { useGetCoursePeriod } from 'api/hooks/private/periods/GetCoursePeriod.ts';

import FillSeat from '../FillSeat';
import Student from '../Student';

export function getFontColor(hexColor: any, threshold = 0.5) {
  const pRed = 0.299;
  const pGreen = 0.587;
  const pBlue = 0.114;
  const color = hexColor.replace('#', '');
  const red = parseInt(color.substr(0, 2), 16);
  const green = parseInt(color.substr(2, 2), 16);
  const blue = parseInt(color.substr(4, 2), 16);

  const contrast = Math.sqrt(pRed * (red / 255) ** 2 + pGreen * (green / 255) ** 2 + pBlue * (blue / 255) ** 2);

  return contrast > threshold ? '#000000' : '#ffffff';
}

type PropTypes = {
  calendarEvent: any;
  coursePeriod: CalendarCoursePeriod;
  editMode: boolean;
  onclickEmptySeat: any;
  onclickStudent: any;
  onclickDrive: any;
  hideFillSeat: boolean | undefined;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export default function Drive(props: PropTypes) {
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    calendarEvent,
    editMode,
    onclickDrive,
    onclickEmptySeat,
    hideFillSeat,
  } = props;
  const { isNewDrive } = calendarEvent.event.toPlainObject().extendedProps;
  // console.log(calendarEvent);
  const viewType = calendarEvent.view.type;
  const shouldEnablePreFetching = !(viewType === 'listMonth' || isNewDrive);
  const navigate = useNavigate();

  // const { data: coursePeriod } = useQuery(['course_period', props.coursePeriod?.id], async () => getCoursePeriod({ id: props.coursePeriod.id }), {
  //   enabled: shouldEnablePreFetching,
  //   refetchOnWindowFocus: false,
  //   refetchOnMount: false,
  //   refetchOnReconnect: false,
  // });

  const { data: coursePeriod } = useGetCoursePeriod({
    variables: {
      id: props.coursePeriod?.id,
    },
    enabled: shouldEnablePreFetching,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const getRegistrationButtons = () => {
    if (!coursePeriod?.period_enrollments) {
      return null;
    }
    const activeRegistrations = coursePeriod.period_enrollments.filter((enrollment) => enrollment.canceled_at === null);

    if (activeRegistrations.length > 0) {
      return activeRegistrations.map((registration) => (
        // <Grid container direction="row" sx={{ px: '4px', opacity: editMode ? '50%' : '100%' }} key={registration.course_period_id + registration.student_user_id}>
        //   <Student
        //     calendarEvent={calendarEvent}
        //     onclickStudent={() => {
        //       if (onclickDrive !== null) {
        //         onclickDrive(coursePeriod);
        //       }
        //     }}
        //     onclickEmptySeat={onclickEmptySeat}
        //     coursePeriodEnrollment={registration}
        //     hideFillSeat={hideFillSeat}
        //   />
        // </Grid>
        // <Grid size={1} key={registration.course_period_id + registration.student_user_id}>
        <Student
          key={registration.student_user_id}
          calendarEvent={calendarEvent}
          onclickStudent={(studentId) => {
            if (onclickDrive !== null) {
              onclickDrive(coursePeriod, studentId);
            }
          }}
          onclickEmptySeat={onclickEmptySeat}
          coursePeriodEnrollment={registration}
          courseType={coursePeriod?.course_section?.course?.course_type}
          hideFillSeat={hideFillSeat}
        />

        // </Grid>
      ));
    }

    if (editMode) {
      return (
        <Grid container direction="row" sx={{ px: '4px' }}>
          <Typography>No Enrollments</Typography>
        </Grid>
      );
    }

    return (
      <Grid container direction="row" sx={{ px: '4px' }}>
        <FillSeat calendarEvent={calendarEvent} onclickEmptySeat={onclickEmptySeat} hideFillSeat={hideFillSeat} />
      </Grid>
    );
  };

  const courseType = coursePeriod?.course_section?.course?.course_type;
  let fontColor;
  let variant: TypographyProps['variant'] = 'body1';

  if (viewType === 'timeGridWeek') {
    variant = 'subtitle2';
  }

  switch (calendarEvent.view.type) {
    case 'listDay':
      fontColor = '#000000';
      break;
    case 'listWeek':
      fontColor = '#000000';
      break;
    case 'listMonth':
      fontColor = '#000000';
      break;
    case 'listYear':
      fontColor = '#000000';
      break;
    default:
      fontColor = getFontColor(calendarEvent.backgroundColor, 0.8);
      break;
  }

  const showTime = !['listWeek', 'listDay'].includes(viewType);
  const showSectionTitle = ['resourceTimeGridDay', 'listWeek', 'listDay'].includes(viewType);
  const showInstructor = ['listWeek', 'listDay', 'timeGridWeek'].includes(viewType);
  const showRegistrations = ['resourceTimeGridDay', 'listWeek', 'listDay'].includes(viewType);

  return (
    <>
      <Stack direction="column" gap={1} px={['resourceTimeGridDay', 'timeGridWeek'].includes(viewType) ? 1 : 0}>
        <Stack direction="row" justifyContent="space-between" flexWrap="wrap" gap={0.5}>
          {showTime && (
            <Typography
              variant={variant}
              // component="div"
              sx={{ color: fontColor, lineHeight: 'normal', textDecoration: 'underline' }}
            >
              {calendarEvent.timeText}
            </Typography>
          )}

          {showSectionTitle && (
            <Typography
              variant={variant}
              // align="right"
              noWrap
              component="div"
              sx={{ color: fontColor, lineHeight: 'normal', textDecoration: 'medium' }}
            >
              {props.coursePeriod?.course_section?.title}
            </Typography>
          )}
        </Stack>

        <Stack direction="row" justifyContent="space-between" flexWrap="wrap-reverse">
          <Box width="100%" flexWrap="nowrap">
            {showRegistrations && courseType === 'behind_the_wheel' && getRegistrationButtons()}
            {['in_person', 'synchronous_online'].includes(courseType ?? '') && (
              <Button
                variant="text"
                sx={{ color: fontColor, lineHeight: 'normal', textDecoration: 'medium' }}
                onClick={() => navigate(`/admin/periods/${props.coursePeriod?.id}`)}
              >
                View Roster
              </Button>
            )}
          </Box>
          {showInstructor && (
            <Typography
              variant={variant}
              component="div"
              sx={{ color: fontColor, lineHeight: 'normal', fontWeight: 'medium' }}
            >
              {calendarEvent.event.toPlainObject().title}
            </Typography>
          )}
        </Stack>
      </Stack>
      {/* <Stack
        direction="row"
        justifyContent="space-between"
        px={['resourceTimeGridDay', 'timeGridWeek'].includes(viewType) ? 1 : 0}
      >
        <Stack gap={1}>
          {showTime && (
            <Typography
              variant={variant}
              component="div"
              sx={{ color: fontColor, lineHeight: 'normal', textDecoration: 'underline' }}
            >
              {calendarEvent.timeText}
            </Typography>
          )}
          {showInstructor && (
            <Typography
              variant={variant}
              component="div"
              sx={{ color: fontColor, lineHeight: 'normal', fontWeight: 'medium' }}
            >
              {calendarEvent.event.toPlainObject().title}
            </Typography>
          )}
          <Box>{showRegistrations && getRegistrationButtons()}</Box>
        </Stack>
        <Stack gap={1}>
          {showSectionTitle && (
            <Typography
              variant={variant}
              align="right"
              component="div"
              sx={{ color: fontColor, lineHeight: 'normal', textDecoration: 'medium' }}
            >
              {props.coursePeriod?.course_section?.title}
            </Typography>
          )}
        </Stack>
      </Stack> */}
    </>
  );
}
